import { useTranslation } from 'react-i18next';
import type { ReactNode } from 'react';
import {
  DndContext,
  KeyboardSensor,
  MouseSensor,
  TouchSensor,
  useDroppable,
  useSensor,
  useSensors,
  type Announcements,
  type DragEndEvent,
  type UniqueIdentifier,
} from '@dnd-kit/core';
import {
  SortableContext,
  rectSortingStrategy,
  sortableKeyboardCoordinates,
} from '@dnd-kit/sortable';
import { Flex, Grid, ImagePicker, Text } from '@/components/common';
import type { ProfilePhoto } from '@/types';
import { SortablePhotoTile } from './SortablePhotoTile';

export interface PhotoGridProps {
  photos: ProfilePhoto[];
  uploading: boolean;
  onUpload: (blob: Blob, filename: string) => Promise<void>;
  onRemove: (index: number) => void | Promise<void>;
  maxPhotos?: number;
  /** Enables drag-to-reorder on filled slots. */
  onReorder?: (fromIndex: number, toIndex: number) => void;
}

const MAX_PHOTOS = 3;
const EMPTY_SLOT_PREFIX = 'empty-slot-';
const SLOT_INDICES = [0, 1, 2];

function SlotPicker({
  idx,
  photos,
  uploading,
  onUpload,
  onRemove,
  maxPhotos,
}: Omit<PhotoGridProps, 'onReorder'> & { idx: number }) {
  const { t } = useTranslation('profile');
  const photo = photos[idx];
  const max = maxPhotos ?? MAX_PHOTOS;
  return (
    <ImagePicker
      value={photo?.url}
      label={t('edit.photos.tileLabel', { index: idx + 1 })}
      onUpload={onUpload}
      onRemove={() => onRemove(idx)}
      disabled={uploading || (!photo && photos.length >= max)}
      size={idx === 0 ? 4 : 2}
    />
  );
}

function StaticPhotoGrid(props: Omit<PhotoGridProps, 'onReorder'>) {
  return (
    <Grid columns="2fr 1fr" rows="2" gap="3">
      {SLOT_INDICES.map(idx => (
        <Flex
          direction="column"
          justify="center"
          key={idx}
          style={{ gridRow: idx === 0 ? 'span 2' : undefined }}
        >
          <SlotPicker {...props} idx={idx} />
        </Flex>
      ))}
    </Grid>
  );
}

/** Empty slot that still accepts drops, mapping to the nearest valid index. */
function EmptyDropSlot({
  id,
  gridRow,
  children,
}: {
  id: string;
  gridRow?: string;
  children: ReactNode;
}) {
  const { setNodeRef, isOver } = useDroppable({ id });
  return (
    <Flex
      direction="column"
      justify="center"
      ref={setNodeRef}
      style={{
        gridRow,
        borderRadius: 'var(--radius-4)',
        boxShadow: isOver ? 'inset 0 0 0 2px var(--accent-9)' : undefined,
      }}
    >
      {children}
    </Flex>
  );
}

function SortablePhotoGrid({
  onReorder,
  ...props
}: PhotoGridProps & { onReorder: NonNullable<PhotoGridProps['onReorder']> }) {
  const { t } = useTranslation('profile');
  const { photos, uploading } = props;

  const sensors = useSensors(
    useSensor(MouseSensor, { activationConstraint: { distance: 4 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 200, tolerance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const photoIndex = (id: UniqueIdentifier) => photos.findIndex(photo => photo.id === id);

  /** 1-based position of a droppable id (photo id or empty-slot marker). */
  const positionLabel = (id: UniqueIdentifier | undefined): number => {
    if (id == null) return 0;
    const raw = String(id);
    if (raw.startsWith(EMPTY_SLOT_PREFIX)) {
      return Math.min(Number(raw.slice(EMPTY_SLOT_PREFIX.length)) + 1, photos.length);
    }
    return photoIndex(id) + 1;
  };

  const canDrag = !uploading && photos.length > 1;

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over) return;
    const from = photoIndex(active.id);
    const raw = String(over.id);
    const to = raw.startsWith(EMPTY_SLOT_PREFIX)
      ? Math.min(Number(raw.slice(EMPTY_SLOT_PREFIX.length)), photos.length - 1)
      : photoIndex(over.id);
    if (from >= 0 && to >= 0 && from !== to) onReorder(from, to);
  };

  const announcements: Announcements = {
    onDragStart: ({ active }) =>
      t('edit.photos.announcePickedUp', { index: positionLabel(active.id) }),
    onDragOver: ({ active, over }) =>
      over
        ? t('edit.photos.announceOver', {
            index: positionLabel(active.id),
            over: positionLabel(over.id),
          })
        : undefined,
    onDragEnd: ({ active, over }) =>
      over
        ? t('edit.photos.announceDropped', {
            index: positionLabel(active.id),
            over: positionLabel(over.id),
          })
        : t('edit.photos.announceCancelled', { index: positionLabel(active.id) }),
    onDragCancel: ({ active }) =>
      t('edit.photos.announceCancelled', { index: positionLabel(active.id) }),
  };

  return (
    <Flex direction="column" gap="2">
      <DndContext
        sensors={sensors}
        accessibility={{
          announcements,
          screenReaderInstructions: { draggable: t('edit.photos.dragInstructions') },
        }}
        onDragEnd={handleDragEnd}
      >
        <SortableContext items={photos.map(photo => photo.id)} strategy={rectSortingStrategy}>
          <Grid columns="2fr 1fr" rows="2" gap="3">
            {SLOT_INDICES.map(idx => {
              const photo = photos[idx];
              const gridRow = idx === 0 ? 'span 2' : undefined;
              if (photo) {
                return (
                  <SortablePhotoTile
                    key={photo.id}
                    photo={photo}
                    index={idx + 1}
                    gridRow={gridRow}
                    disabled={!canDrag}
                  >
                    <SlotPicker {...props} idx={idx} />
                  </SortablePhotoTile>
                );
              }
              return (
                <EmptyDropSlot
                  key={`${EMPTY_SLOT_PREFIX}${idx}`}
                  id={`${EMPTY_SLOT_PREFIX}${idx}`}
                  gridRow={gridRow}
                >
                  <SlotPicker {...props} idx={idx} />
                </EmptyDropSlot>
              );
            })}
          </Grid>
        </SortableContext>
      </DndContext>
      {photos.length > 1 && (
        <Text size="1" color="gray">
          {t('edit.photos.reorderHint')}
        </Text>
      )}
    </Flex>
  );
}

export function PhotoGrid(props: PhotoGridProps) {
  if (!props.onReorder) return <StaticPhotoGrid {...props} />;
  return <SortablePhotoGrid {...props} onReorder={props.onReorder} />;
}
